import { Component, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EMPTY, switchMap } from 'rxjs';
import { ERROR_MESSAGE, ValidationErrorMessages } from 'src/app/_helpers/utils';
import { FormBase } from 'src/app/_models/form-base';
@Component({
    selector: 'app-chip-checkbox-group',
    templateUrl: './chip-checkbox-group.component.html',
    styleUrls: ['./chip-checkbox-group.component.scss'],
    imports: [FormsModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChipCheckboxGroupComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Checkbox base field (required) */
  field = input<FormBase<string>>();

  /** Parent FormGroup containing the control (required) */
  parentForm = input<FormGroup>();

  constructor() {
    toObservable(this.parentForm)
      .pipe(
        switchMap((form) => (form ? form.valueChanges : EMPTY)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.cdr.markForCheck());
  }

  /** Set whether checkboxes are disabled (optional, default: false) */
  isDisabled = input<boolean>(false);

  /** Set checkbox style (optional, default: 'tabs') */
  checkboxStyle = input<'tabs'>('tabs');

  /** Emitted when a checkbox is clicked */
  checkboxClicked = output<Event>();

  updateValue(event: Event): void {
    this.checkboxClicked.emit(event);
  }

  getChecked(index: number): boolean {
    const controlAccessor = this.field().key;
    const arrayKey = 'controls';
    const arrayIndex = index;
    return this.parentForm()?.controls?.[controlAccessor]?.[arrayKey]?.[arrayIndex]
      ?.value as boolean;
  }

  public get hasErrors(): boolean {
    return (
      !!this.parentForm()?.controls?.[this.field().key] &&
      !!this.parentForm()?.controls?.[this.field().key]?.errors &&
      !!this.parentForm()?.controls?.[this.field().key]?.touched
    );
  }

  public get errorsList(): string[] {
    const errors = this.parentForm()?.controls?.[this.field().key]?.errors ?? {};
    return Object.keys(errors).map((err) => {
      const key = err as keyof ValidationErrorMessages;
      const messageFn = ERROR_MESSAGE[key];
      return messageFn(
        this.parentForm()?.controls?.[this.field().key]?.getError(err) as never,
      );
    });
  }
}
